import { useState } from "react";
import type { FastProject } from "../../../types/interfaces";
import { useFinalizeProjectMutation } from "../../../app/features/project/projectApiSlice";
import useErrorMessage from "../../../utils/useErrorMessage";

interface UseFinalizeProjectReturn {
  isFinalizeDialogOpen: boolean;
  handleCancel: () => void;
  handleFinalize: () => void;
  showFinalizeModal: (project: FastProject) => void;
  isLoadingFinalize: boolean;

  finalizeProjectErrorMsg: string;
  isFinalizeDialogErrorMessageOpen: boolean;
  closeFinalizeProjectErrorMessage: () => void;
}

const useFinalizeProject = (refetch?: () => void): UseFinalizeProjectReturn => {
  const [isFinalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [toFinalizeId, setToFinalizeId] = useState<number | null>(null);

  const [finalizeProject, { isLoading: isLoadingFinalize, isError, error }] = useFinalizeProjectMutation();
  const { errorMsg } = useErrorMessage({ isError, error });

  const [isFinalizeDialogErrorMessageOpen, setFinalizeDialogErrorMessageOpen] = useState(false);
  const closeFinalizeProjectErrorMessage = () => setFinalizeDialogErrorMessageOpen(false);

  const handleCancel = () => setFinalizeDialogOpen(false);
  const handleFinalize = async () => {
    if (isLoadingFinalize || !toFinalizeId) return;
    try {
      await finalizeProject(toFinalizeId).unwrap();
      if (refetch) refetch();
    } catch (error) {
      console.error('Failed to finalize project:', error);
    } finally {
      setFinalizeDialogOpen(false);
    }
  }
  const showFinalizeModal = (project: FastProject) => {
    setFinalizeDialogOpen(true);
    setToFinalizeId(project.id);
  }

  return {
    isFinalizeDialogOpen,
    handleCancel,
    handleFinalize,
    showFinalizeModal,
    isLoadingFinalize,
    finalizeProjectErrorMsg: errorMsg,
    isFinalizeDialogErrorMessageOpen,
    closeFinalizeProjectErrorMessage
  }
}

export default useFinalizeProject;
